import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Divider, Avatar, Chip, Card, CardContent,
  IconButton, Button, TextField, InputAdornment, Tooltip, MenuItem,
  Select, FormControl, InputLabel, Pagination, Snackbar, Alert,
  Paper, Stack, useTheme, useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SchoolIcon from '@mui/icons-material/School';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BadgeIcon from '@mui/icons-material/Badge';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import AuthService from '../Auth/AuthService';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

// ---------- Helpers ----------

const typeMeta = (type) => {
  switch (type) {
    case 'contactus':
      return { label: 'Contact Us', color: '#2563eb', bg: '#dbeafe', icon: <EmailIcon fontSize="small" /> };
    case 'feedback':
      return { label: 'Feedback', color: '#0891b2', bg: '#cffafe', icon: <FeedbackIcon fontSize="small" /> };
    case 'suggestion':
      return { label: 'Suggestion', color: '#ea580c', bg: '#ffedd5', icon: <LightbulbIcon fontSize="small" /> };
    default:
      return { label: type, color: '#64748b', bg: '#f1f5f9', icon: <BadgeIcon fontSize="small" /> };
  }
};

const colorFor = (name = '') => {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5'];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return palette[sum % palette.length];
};

const formatMobile = (number) => {
  if (!number) return '—';
  const str = String(number);
  if (str.length === 10) return `${str.slice(0, 3)}-${str.slice(3, 6)}-${str.slice(6)}`;
  return str;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------- Sub-components ----------

const InfoBlock = ({ icon, label, value, color = 'text.secondary' }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Box sx={{ color, mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'text.secondary',
          fontSize: 10.5,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word', mt: 0.25 }}>
        {value || '—'}
      </Typography>
    </Box>
  </Stack>
);

// ---------- Page ----------

const ContactViewPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [page, setPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const rowsPerPage = 6;
  const fetchedRef = useRef(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const url = AuthService.pathUrl('GET', '/contacts');
      const response = await AuthService.getAuth(url);
      setContacts(response || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setSnackbar({ open: true, message: 'Failed to load contacts', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchContacts();
  }, []);

  // ---------- Filtering / pagination ----------
  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      if (selectedType !== 'all' && !(contact.contactType || []).includes(selectedType)) {
        return false;
      }
      if (searchTerm.trim()) {
        const s = searchTerm.toLowerCase();
        return (
          (contact.fullName || '').toLowerCase().includes(s) ||
          (contact.organization || '').toLowerCase().includes(s) ||
          (contact.cityState || '').toLowerCase().includes(s) ||
          (contact.description || '').toLowerCase().includes(s) ||
          (contact.contactType || []).some((t) => t.toLowerCase().includes(s))
        );
      }
      return true;
    });
  }, [contacts, searchTerm, selectedType]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedType]);

  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const count = (type) =>
      contacts.filter((c) => (c.contactType || []).includes(type)).length;
    return {
      total: contacts.length,
      contactus: count('contactus'),
      feedback: count('feedback'),
      suggestion: count('suggestion'),
    };
  }, [contacts]);

  // ---------- Handlers ----------
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setPage(1);
  };

  return (
    <PageShell
      icon={<ContactMailIcon />}
      title="Contact Management"
      subtitle="Enquiries, feedback and suggestions submitted by users"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Contacts' }]}
      actions={
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchContacts}
            disabled={loading}
            sx={{
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Contacts" value={stats.total} icon={<ContactMailIcon />} color="#2563eb" />
        <StatCard label="Contact Us" value={stats.contactus} icon={<EmailIcon />} color="#2563eb" />
        <StatCard label="Feedback" value={stats.feedback} icon={<FeedbackIcon />} color="#0891b2" />
        <StatCard label="Suggestions" value={stats.suggestion} icon={<LightbulbIcon />} color="#ea580c" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Contact Type</InputLabel>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            label="Contact Type"
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            }
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="contactus">Contact Us</MenuItem>
            <MenuItem value="feedback">Feedback</MenuItem>
            <MenuItem value="suggestion">Suggestion</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search name, organization, city, description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {(searchTerm || selectedType !== 'all') && (
          <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
            Clear
          </Button>
        )}
      </Paper>

      {/* Results summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing <strong>{paginatedContacts.length}</strong> of{' '}
        <strong>{filteredContacts.length}</strong>{' '}
        {filteredContacts.length === 1 ? 'contact' : 'contacts'}
        {searchTerm && ` matching "${searchTerm}"`}
      </Typography>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: '4px solid', borderColor: 'primary.light', borderTopColor: 'primary.main', animation: 'spin 1s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      ) : filteredContacts.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<ContactMailIcon />}
            title={contacts.length === 0 ? 'No contacts yet' : 'No matches'}
            subtitle={
              contacts.length === 0
                ? 'Contact form submissions will appear here.'
                : 'Try a different search term or clear the filters.'
            }
            actionLabel={contacts.length > 0 ? 'Clear Filters' : undefined}
            onAction={clearFilters}
          />
        </Paper>
      ) : (
        <>
          <Grid container spacing={2.5}>
            {paginatedContacts.map((contact) => {
              const types = (contact.contactType || []).map(typeMeta);
              const avatarColor = colorFor(contact.fullName || '');
              return (
                <Grid item xs={12} key={contact.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all .15s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 8px 24px rgba(37,99,235,0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                      {/* Header row */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar
                            sx={{
                              bgcolor: avatarColor,
                              width: 48,
                              height: 48,
                              fontWeight: 700,
                              fontSize: 18,
                            }}
                          >
                            {(contact.fullName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {contact.fullName || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID #{String(contact.id).padStart(6, '0')}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ rowGap: 1 }}
                        >
                          {types.map((t, idx) => (
                            <Chip
                              key={idx}
                              icon={t.icon}
                              label={t.label}
                              size="small"
                              sx={{
                                bgcolor: t.bg,
                                color: t.color,
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: t.color },
                              }}
                            />
                          ))}
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      {/* Detail grid */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoBlock
                            icon={<SchoolIcon fontSize="small" />}
                            label="Organization"
                            value={contact.organization}
                            color="#2563eb"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoBlock
                            icon={<PhoneIcon fontSize="small" />}
                            label="Mobile"
                            value={formatMobile(contact.mobile)}
                            color="#7c3aed"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoBlock
                            icon={<LocationOnIcon fontSize="small" />}
                            label="City / State"
                            value={contact.cityState}
                            color="#db2777"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoBlock
                            icon={<CalendarTodayIcon fontSize="small" />}
                            label="Submitted"
                            value={formatDate(contact.createdAt)}
                            color="#0891b2"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'grey.50',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  fontSize: 10.5,
                                }}
                              >
                                Description
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                            >
                              {contact.description || 'No description provided.'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Actions */}
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        sx={{ mt: 2 }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => navigate(`/contact/view/${contact.id}`)}
                        >
                          View Details
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                size={isMobile ? 'medium' : 'large'}
                showFirstButton
                showLastButton
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default ContactViewPage;